import React, { useState, useEffect } from 'react';
import { toLocalYMD } from '../lib/dates';
import { useParams, useSearchParams } from 'react-router-dom';
import { 
  Download, Printer, CreditCard, Building2, 
  CheckCircle, Clock, AlertTriangle, Mail,
  Phone, MapPin, Calendar, FileText, Shield,
  ChevronRight, Star, ExternalLink, Loader2, Banknote
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CustomerInvoicePage = () => {
  const { invoiceId } = useParams();
  const [searchParams] = useSearchParams();
  const [invoice, setInvoice] = useState(null);
  const [customer, setCustomer] = useState(null);
  const [businessSettings, setBusinessSettings] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [paymentSuccess, setPaymentSuccess] = useState(false);
  const [paymentStatus, setPaymentStatus] = useState(null);
  const [saveCardForFuture, setSaveCardForFuture] = useState(false);

  // Check for payment return from Stripe
  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    const status = searchParams.get('status');
    
    if (sessionId && status === 'success') {
      // Poll for payment status
      pollPaymentStatus(sessionId);
    }
  }, [searchParams]);

  // Fetch invoice data
  useEffect(() => {
    const fetchInvoice = async () => {
      setIsLoading(true);
      try {
        const response = await fetch(`${API_URL}/api/invoices/${invoiceId}`);
        if (!response.ok) throw new Error('Invoice not found');
        const data = await response.json();
        setInvoice(data);
        
        // Fetch customer if we have customer_id
        if (data.customer_id) {
          const custResponse = await fetch(`${API_URL}/api/customers/${data.customer_id}`);
          if (custResponse.ok) {
            const custData = await custResponse.json();
            setCustomer(custData);
          }
        }
        
        // Fetch business settings (for company logo and info)
        if (data.user_id) {
          try {
            const settingsResponse = await fetch(`${API_URL}/api/settings/public/${data.user_id}`);
            if (settingsResponse.ok) {
              const settingsData = await settingsResponse.json();
              setBusinessSettings(settingsData);
            }
          } catch (err) {
            console.log('Could not load business settings');
          }
        }
      } catch (err) {
        console.error('Failed to fetch invoice:', err);
        // Surface the real error to the customer instead of pretending the
        // invoice exists. Clicking "Pay by Card" on a fake demo invoice
        // produced a confusing "Payment service error" alert.
        setError(
          err.message && err.message.toLowerCase().includes('not found')
            ? 'This invoice link is invalid or has expired. Please contact the business for a new link.'
            : 'We could not load this invoice. Please try again, or contact the business.'
        );
        setInvoice(null);
        setCustomer(null);
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchInvoice();
  }, [invoiceId]);

  // Poll payment status
  const pollPaymentStatus = async (sessionId, attempts = 0) => {
    const maxAttempts = 5;
    
    if (attempts >= maxAttempts) {
      setPaymentStatus('timeout');
      return;
    }
    
    try {
      const response = await fetch(`${API_URL}/api/payments/status/${sessionId}`);
      if (!response.ok) throw new Error('Failed to check status');
      
      const data = await response.json();
      setPaymentStatus(data.payment_status);
      
      if (data.payment_status === 'paid') {
        setPaymentSuccess(true);
        // Refresh invoice
        const invResponse = await fetch(`${API_URL}/api/invoices/${invoiceId}`);
        if (invResponse.ok) {
          setInvoice(await invResponse.json());
        }
      } else if (data.status !== 'expired') {
        // Continue polling
        setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), 2000);
      }
    } catch (err) {
      console.error('Payment status check failed:', err);
    }
  };

  // Handle payment initiation
  const handlePayment = async (method) => {
    setIsProcessing(true);
    setPaymentMethod(method);
    
    try {
      const response = await fetch(`${API_URL}/api/payments/create-checkout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          invoice_id: invoice.id || invoiceId,
          origin_url: window.location.origin,
          payment_method: method,
          save_card_for_future: saveCardForFuture,
          customer_id: invoice.customer_id
        })
      });
      
      let data;
      try {
        data = await response.json();
      } catch {
        data = { detail: 'Payment service error' };
      }
      
      if (!response.ok) {
        // Handle specific error cases
        if (response.status === 400 && data.detail?.includes('mandate')) {
          alert('To pay by Direct Debit, you need to set up a mandate first. Please contact the business to set this up.');
          return;
        }
        if (response.status === 400 && data.detail?.includes('not connected')) {
          alert('Direct Debit payments are not currently available. Please use card payment.');
          return;
        }
        throw new Error(data.detail || 'Payment failed');
      }
      
      // Handle redirect for GoCardless billing request flow or Stripe checkout
      if (data.redirect_url) {
        window.location.href = data.redirect_url;
      } else if (data.checkout_url) {
        window.location.href = data.checkout_url;
      }
    } catch (err) {
      console.error('Payment error:', err);
      alert(err.message || 'Payment failed. Please try again.');
    } finally {
      setIsProcessing(false);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-zinc-100 flex items-center justify-center">
        <div className="text-center">
          <Loader2 size={40} className="text-emerald-500 animate-spin mx-auto mb-4" />
          <p className="text-zinc-600">Loading invoice...</p>
        </div>
      </div>
    );
  }

  // Real error state — invoice didn't load. Show a clear message rather
  // than rendering a half-broken invoice page.
  if (error || !invoice) {
    return (
      <div className="min-h-screen bg-zinc-100 flex items-center justify-center p-6">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-sm p-8 text-center">
          <div className="w-16 h-16 bg-amber-50 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <AlertTriangle size={28} className="text-amber-500" />
          </div>
          <h1 className="text-xl font-bold text-zinc-900 mb-2">Invoice unavailable</h1>
          <p className="text-zinc-600 text-sm leading-relaxed">
            {error || 'We could not load this invoice.'}
          </p>
          <p className="text-zinc-400 text-xs mt-6">
            If you believe this is a mistake, please contact the business.
          </p>
        </div>
      </div>
    );
  }

  const isPaid = invoice?.status === 'paid' || paymentSuccess;

  const statusConfig = {
    paid: { bg: 'bg-emerald-50', text: 'text-emerald-700', icon: CheckCircle, label: 'Paid' },
    pending: { bg: 'bg-amber-50', text: 'text-amber-700', icon: Clock, label: 'Awaiting Payment' },
    overdue: { bg: 'bg-red-50', text: 'text-red-700', icon: AlertTriangle, label: 'Overdue' },
  };

  const status = statusConfig[invoice?.status] || statusConfig.pending;
  const StatusIcon = status.icon;

  const handlePrint = () => {
    window.print();
  };

  const handleDownloadPDF = async () => {
    // Call backend API to generate PDF
    try {
      const response = await fetch(`${API_URL}/api/invoices/${invoiceId}/pdf`);
      if (!response.ok) {
        throw new Error('Failed to generate PDF');
      }
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${businessName}-invoice-${invoiceNumber}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      console.error('PDF download error:', err);
      alert('Failed to download PDF. Please try again or use Print instead.');
    }
  };

  // Helper to get values safely
  const invoiceTotal = invoice?.total || 0;
  const invoiceNumber = invoice?.invoice_number || invoice?.id || 'N/A';
  const customerName = customer?.name || invoice?.customer_name || 'Customer';
  const customerEmail = customer?.email || invoice?.customer_email || '';
  const customerPhone = customer?.phone || invoice?.customer_phone || '';
  const customerAddress = customer?.address || invoice?.customer_address || '';
  const invoiceDate = invoice?.created_at?.split('T')[0] || toLocalYMD(new Date());
  const dueDate = invoice?.due_date || '';
  
  // BACS reference: customer's first line of address (so the bank statement
  // tells the user WHICH property paid). Falls back to invoice number when no
  // address. Capped at 18 chars (UK BACS limit).
  const bacsReference = (() => {
    if (customerAddress) {
      const firstLine = customerAddress.replace(/\r/g, '\n').split('\n')[0].split(',')[0].trim();
      const cleaned = firstLine.replace(/[^a-zA-Z0-9 ]/g, '').replace(/\s+/g, ' ').trim();
      if (cleaned) return cleaned.slice(0, 18);
    }
    return invoiceNumber;
  })();
  
  // Business info from settings or defaults
  const businessName = businessSettings?.company_name || 'StreakFree';
  const businessLogo = businessSettings?.company_logo || null;
  const googleReviewsUrl = businessSettings?.google_reviews_url || null;
  const websiteUrl = businessSettings?.website_url || null;
  const facebookUrl = businessSettings?.facebook_url || null;
  const instagramUrl = businessSettings?.instagram_url || null;
  
  // Payment options from settings (default to true/show)
  const showCardPayment = businessSettings?.invoice_show_card_payment !== false;
  const showDDPayment = businessSettings?.invoice_show_dd_payment !== false;
  const showBACS = businessSettings?.invoice_show_bacs !== false;
  
  // Business info object for the "From" section
  const businessInfo = {
    name: businessSettings?.company_name || 'Perfect Panes',
    address: businessSettings?.business_address || 'West Sussex, UK',
    email: businessSettings?.business_email || 'info@perfect-panes.com',
    phone: businessSettings?.business_phone || '',
    vat: businessSettings?.vat_number || null,
    bankName: businessSettings?.bank_name || 'Your Bank',
    sortCode: businessSettings?.sort_code || '00-00-00',
    accountNumber: businessSettings?.account_number || '00000000',
    accountName: businessSettings?.account_name || businessSettings?.company_name || 'Perfect Panes'
  };

  if (isPaid) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-zinc-50 to-zinc-100 flex items-center justify-center p-4">
        <div className="bg-white rounded-3xl shadow-xl p-8 max-w-md w-full text-center">
          <div className="w-20 h-20 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle size={40} className="text-emerald-600" />
          </div>
          <h1 className="text-2xl font-bold text-zinc-900 mb-2">Payment Successful!</h1>
          <p className="text-zinc-600 mb-6">
            Thank you for your payment of <span className="font-semibold">£{invoiceTotal.toFixed(2)}</span>
          </p>
          <p className="text-sm text-zinc-500 mb-8">
            A confirmation email has been sent to {customerEmail || 'your email'}
          </p>
          <div className="p-4 bg-zinc-50 rounded-xl text-left">
            <p className="text-xs text-zinc-500 mb-1">Invoice Reference</p>
            <p className="font-mono font-semibold text-zinc-900">{invoiceNumber}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div 
      className="bg-zinc-100" 
      data-testid="customer-invoice-page" 
      style={{ 
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        overflowY: 'auto',
        overflowX: 'hidden',
        WebkitOverflowScrolling: 'touch',
        zIndex: 9999
      }}
    >
      {/* Main Invoice Container */}
      <div className="max-w-3xl mx-auto p-4 md:p-8 pb-24">
        {/* Invoice Card */}
        <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
          
          {/* Header with Logo */}
          <div className="p-6 md:p-8 border-b border-zinc-200">
            <div className="flex items-start justify-between">
              <div>
                {businessLogo ? (
                  <img 
                    src={businessLogo} 
                    alt={businessName}
                    className="h-16 md:h-20 w-auto object-contain mb-2"
                  />
                ) : (
                  <div className="h-16 w-16 bg-emerald-500 rounded-xl flex items-center justify-center font-bold text-2xl text-white mb-2">
                    {businessName.substring(0, 2).toUpperCase()}
                  </div>
                )}
                <p className="text-zinc-500 text-sm">#{invoiceNumber}</p>
              </div>
              <div className="text-right">
                <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-full ${status.bg} mb-2`}>
                  <StatusIcon size={16} className={status.text} />
                  <span className={`text-sm font-semibold ${status.text}`}>{status.label}</span>
                </div>
                <h1 className="text-3xl font-bold text-zinc-900">INVOICE</h1>
              </div>
            </div>
          </div>

          {/* Amount Due Section */}
          <div className="bg-gradient-to-r from-emerald-600 to-emerald-700 p-6 md:p-8">
            <div className="text-center text-white">
              <p className="text-emerald-100 text-sm mb-2">Amount Due</p>
              <p className="text-5xl font-bold mb-2">£{invoiceTotal.toFixed(2)}</p>
              <p className="text-emerald-100">
                Due by {dueDate ? new Date(dueDate).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' }) : 'N/A'}
              </p>
            </div>
          </div>

          {/* Payment Buttons - Direct on page */}
          {!isPaid && (
            <div className="p-6 md:p-8 bg-zinc-50 border-b border-zinc-200">
              <div className="grid md:grid-cols-2 gap-4">
                {showCardPayment && (
                  <button
                    onClick={() => handlePayment('stripe')}
                    disabled={isProcessing}
                    className="flex items-center justify-center gap-3 py-4 px-6 bg-blue-600 text-white rounded-xl font-semibold hover:bg-blue-700 transition-colors disabled:opacity-50"
                    data-testid="pay-card-btn"
                  >
                    {isProcessing && paymentMethod === 'stripe' ? (
                      <Loader2 size={20} className="animate-spin" />
                    ) : (
                      <CreditCard size={20} />
                    )}
                    Pay by Card
                  </button>
                )}
                {showDDPayment && (
                  <button
                    onClick={() => handlePayment('gocardless')}
                    disabled={isProcessing}
                    className="flex items-center justify-center gap-3 py-4 px-6 bg-emerald-600 text-white rounded-xl font-semibold hover:bg-emerald-700 transition-colors disabled:opacity-50"
                    data-testid="pay-dd-btn"
                  >
                    {isProcessing && paymentMethod === 'gocardless' ? (
                      <Loader2 size={20} className="animate-spin" />
                    ) : (
                      <Banknote size={20} />
                    )}
                    Pay by Direct Debit
                  </button>
                )}
              </div>
              {showCardPayment && (
                <label className="flex items-center gap-3 mt-4 p-3 bg-white border border-zinc-200 rounded-lg cursor-pointer hover:bg-zinc-50">
                  <input
                    type="checkbox"
                    checked={saveCardForFuture}
                    onChange={(e) => setSaveCardForFuture(e.target.checked)}
                    className="w-5 h-5 rounded border-zinc-300 text-emerald-600 focus:ring-emerald-500"
                  />
                  <span className="text-sm text-zinc-700">Save my card for automatic future payments</span>
                </label>
              )}
            </div>
          )}

          {/* Invoice Details */}
          <div className="p-6 md:p-8">
            <div className="grid md:grid-cols-2 gap-8 mb-8">
              {/* Bill To */}
              <div>
                <h3 className="text-xs font-semibold text-zinc-400 uppercase tracking-wider mb-3">Bill To</h3>
                <p className="font-semibold text-zinc-900 text-lg">{customerName}</p>
                {customerAddress && (
                  <p className="text-zinc-600 mt-1 whitespace-pre-line">{customerAddress}</p>
                )}
                {customerEmail && (
                  <p className="text-zinc-500 text-sm mt-2">{customerEmail}</p>
                )}
              </div>

              {/* Invoice Info */}
              <div className="text-right">
                <h3 className="text-xs font-semibold text-zinc-400 uppercase tracking-wider mb-3">Invoice Details</h3>
                <p className="text-zinc-600"><span className="text-zinc-400">Invoice #:</span> {invoiceNumber}</p>
                <p className="text-zinc-600"><span className="text-zinc-400">Date:</span> {new Date(invoiceDate).toLocaleDateString('en-GB')}</p>
                <p className="text-zinc-600"><span className="text-zinc-400">Due:</span> {dueDate ? new Date(dueDate).toLocaleDateString('en-GB') : 'N/A'}</p>
              </div>
            </div>

            {/* Line Items Table */}
            <div className="border border-zinc-200 rounded-xl overflow-hidden mb-8">
              <table className="w-full">
                <thead className="bg-zinc-50">
                  <tr>
                    <th className="text-left py-3 px-4 text-xs font-semibold text-zinc-500 uppercase">Description</th>
                    <th className="text-center py-3 px-4 text-xs font-semibold text-zinc-500 uppercase">Qty</th>
                    <th className="text-right py-3 px-4 text-xs font-semibold text-zinc-500 uppercase">Price</th>
                    <th className="text-right py-3 px-4 text-xs font-semibold text-zinc-500 uppercase">Amount</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-zinc-100">
                  {(invoice?.items && invoice.items.length > 0) ? (
                    invoice.items.map((item, idx) => (
                      item.is_header ? (
                        <tr key={idx} className="bg-zinc-50">
                          <td colSpan={4} className="py-3 px-4 font-semibold text-zinc-900" data-testid="invoice-item-header">{item.description}</td>
                        </tr>
                      ) : (
                      <tr key={idx}>
                        <td className="py-4 px-4 text-zinc-900">{item.description}</td>
                        <td className="py-4 px-4 text-center text-zinc-600">{item.quantity || 1}</td>
                        <td className="py-4 px-4 text-right text-zinc-600">£{(item.unit_price || item.price || 0).toFixed(2)}</td>
                        <td className="py-4 px-4 text-right font-medium text-zinc-900">£{((item.quantity || 1) * (item.unit_price || item.price || 0)).toFixed(2)}</td>
                      </tr>
                      )
                    ))
                  ) : (
                    <tr>
                      <td className="py-4 px-4 text-zinc-900">{invoice?.service || 'Service'}</td>
                      <td className="py-4 px-4 text-center text-zinc-600">1</td>
                      <td className="py-4 px-4 text-right text-zinc-600">£{(invoice?.subtotal || invoiceTotal).toFixed(2)}</td>
                      <td className="py-4 px-4 text-right font-medium text-zinc-900">£{(invoice?.subtotal || invoiceTotal).toFixed(2)}</td>
                    </tr>
                  )}
                </tbody>
              </table>
              
              {/* Totals */}
              <div className="bg-zinc-50 p-4">
                <div className="max-w-xs ml-auto space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-zinc-500">Subtotal</span>
                    <span className="text-zinc-900">£{(invoice?.subtotal || 0).toFixed(2)}</span>
                  </div>
                  {invoice?.vat_rate > 0 && (
                    <div className="flex justify-between text-sm">
                      <span className="text-zinc-500">VAT ({invoice?.vat_rate}%)</span>
                      <span className="text-zinc-900">£{(invoice?.vat_amount || 0).toFixed(2)}</span>
                    </div>
                  )}
                  <div className="flex justify-between pt-2 border-t border-zinc-200">
                    <span className="font-bold text-zinc-900">Total</span>
                    <span className="font-bold text-xl text-emerald-600">£{invoiceTotal.toFixed(2)}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Invoice notes (the customer-facing "Notes for invoice" field
                entered on the job). Rendered between the line items and
                BACS so it's right under the eye-line as the customer
                reviews the bill. */}
            {invoice?.notes && (
              <div className="bg-amber-50 border-l-4 border-amber-400 rounded-r-xl p-4 mb-8" data-testid="invoice-notes">
                <h4 className="text-sm font-semibold text-amber-900 mb-1">Notes</h4>
                <p className="text-sm text-amber-900 whitespace-pre-wrap">{invoice.notes}</p>
              </div>
            )}

            {/* BACS Details */}
            {showBACS && (
              <div className="bg-zinc-50 border border-zinc-200 rounded-xl p-6 mb-8">
                <h3 className="font-semibold text-zinc-900 mb-4 flex items-center gap-2">
                  <Building2 size={18} className="text-zinc-400" />
                  Pay by Bank Transfer (BACS)
                </h3>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-zinc-400">Account Name</p>
                    <p className="font-medium text-zinc-900">{businessInfo.accountName || businessName}</p>
                  </div>
                  <div>
                    <p className="text-zinc-400">Sort Code</p>
                    <p className="font-mono font-medium text-zinc-900">{businessInfo.sortCode || 'Not configured'}</p>
                  </div>
                  <div>
                    <p className="text-zinc-400">Account Number</p>
                    <p className="font-mono font-medium text-zinc-900">{businessInfo.accountNumber || 'Not configured'}</p>
                  </div>
                  <div>
                    <p className="text-zinc-400">Reference</p>
                    <p className="font-mono font-medium text-zinc-900" data-testid="bacs-reference">{bacsReference}</p>
                  </div>
                </div>
              </div>
            )}

            {/* From Section */}
            <div className="border-t border-zinc-200 pt-6">
              <div className="flex items-start gap-4">
                {businessLogo && (
                  <img src={businessLogo} alt={businessName} className="h-12 w-auto object-contain" />
                )}
                <div className="text-sm text-zinc-500">
                  <p className="font-semibold text-zinc-700">{businessInfo.name}</p>
                  <p>{businessInfo.address}</p>
                  <p>{businessInfo.email}</p>
                  {businessInfo.vat && <p>VAT: {businessInfo.vat}</p>}
                </div>
              </div>
            </div>
          </div>

          {/* Footer Actions */}
          <div className="p-4 bg-zinc-50 border-t border-zinc-200 flex items-center justify-center gap-4">
            <button
              onClick={handlePrint}
              className="flex items-center gap-2 px-4 py-2 text-zinc-600 hover:text-zinc-900 hover:bg-white rounded-lg transition-colors"
            >
              <Printer size={18} />
              Print
            </button>
            <button
              onClick={handleDownloadPDF}
              className="flex items-center gap-2 px-4 py-2 text-zinc-600 hover:text-zinc-900 hover:bg-white rounded-lg transition-colors"
            >
              <Download size={18} />
              Download PDF
            </button>
          </div>
        </div>

        {/* Google Review - if configured */}
        {googleReviewsUrl && (
          <div className="mt-6 bg-white rounded-xl p-6 text-center shadow-sm">
            <p className="text-zinc-600 mb-4">Happy with our service? We'd love your feedback!</p>
            <a 
              href={googleReviewsUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-6 py-3 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors font-medium"
            >
              <Star size={18} className="fill-white" />
              Leave us a Google Review
            </a>
          </div>
        )}

        {/* Footer */}
        <div className="mt-6 text-center text-sm text-zinc-400">
          <div className="flex items-center justify-center gap-1">
            <Shield size={14} />
            <span>Secure Payment Processing</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CustomerInvoicePage;
